import {
  DataTable,
  FlexBox,
  IconButton,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import type { AsyncStatus, FlowUsage } from 'lib-shared-types-api';

import { AppLink } from 'components/appLink';

import styles from './table.module.scss';

type Props = {
  usage: readonly FlowUsage[];
  filename: string;
  type: string;
  asyncStatus: AsyncStatus;
};

const loadingMessage = (
  <div className={styles.messageContainer}>
    <Spinner className={styles.messageSpinner} size={32} />
    <div className={styles.messageText}>Loading audio file usage...</div>
  </div>
);

const errorMessage = (
  <div className={styles.messageContainer}>
    <div className={styles.messageText}>
      An error occurred while loading the audio file usage.
    </div>
  </div>
);

const noAudioFilesMessage = (
  <div className={styles.messageContainer}>
    <div className={styles.messageText}>
      Audio file is not attached to any workflow.
    </div>
  </div>
);

const renderLocation = ({ name, active }: FlowUsage): JSX.Element => (
  <FlexBox alignItems="center">
    <div className={classnames(styles.dot, { [styles.active]: active })}></div>
    <div className={styles.name}>{name}</div>
  </FlexBox>
);

const renderLink = ({
  id,
  type,
  filename,
}: FlowUsage & { type: string; filename: string }): JSX.Element => (
  <Tooltip content="View flow" dark>
    {id === 'default' && (
      <AppLink path="/settings/phone">
        <IconButton icon="external-link" variant="tertiary" />
      </AppLink>
    )}

    {id !== 'default' && (
      <AppLink
        path="/settings/phone/flow/:id"
        onClick={() => ampli.settingsAudioUsageFlowClicked({ type, filename })}
        params={{ path: { id } }}
      >
        <IconButton icon="external-link" variant="tertiary" />
      </AppLink>
    )}
  </Tooltip>
);

export const FileUsagePageTable = ({
  type,
  filename,
  usage,
  asyncStatus,
}: Props): JSX.Element => {
  if (asyncStatus === 'rejected') {
    return errorMessage;
  }

  if (!usage.length) {
    if (asyncStatus === 'pending') {
      return loadingMessage;
    }

    return noAudioFilesMessage;
  }

  return (
    <DataTable
      data={usage}
      columns={[
        {
          label: 'Location',
          selector: renderLocation,
        },
        {
          style: { width: 1 },
          selector: (data) => renderLink({ ...data, type, filename }),
        },
      ]}
    />
  );
};
